/**
 * Alshallal Pastry Company HR System - Team Leave Management (direct manager)
 *
 * Owner's requirement (2026-11): one place where a direct manager sees every
 * leave request of their team - employee, type, dates, days, submission date
 * and status (pending, approved, rejected, cancelled) - filtered by status,
 * employee or leave type, without opening each employee. A pending request can
 * be deleted from here with a written reason (ManagerDeleteLeaveButton).
 *
 * The team is resolved on the server (GET /api/leave/team/requests): direct and
 * approving reports, plus anyone whose request was routed to this manager.
 *
 * 2026-11: a National ID / Iqama search finds one team member's requests
 * directly (POST /api/leave/team/requests/by-id-number). The number goes in the
 * request body, never the URL, and the server searches only this manager's team.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  AlertCircle, Ban, CalendarClock, CheckCircle, ChevronLeft, ChevronRight, Clock, Eye, Filter, RefreshCw, Search, Trash2, X, XCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { leaveDisplayStatus } from '@/lib/requestLifecycle';
import { LeaveRequestDetailsDialog } from '@/components/leave/LeaveRequestDetailsDialog';
import { ManagerDeleteLeaveButton } from '@/components/leave/ManagerDeleteLeaveButton';
import { TeamBalancesCard } from '@/components/leave/BalanceExceptions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  pending_manager: { bg: 'bg-amber-100 text-amber-800', icon: Clock, en: 'Pending manager', ar: 'بانتظار المدير' },
  pending_hr: { bg: 'bg-blue-100 text-blue-800', icon: Clock, en: 'Pending HR', ar: 'بانتظار الموارد البشرية' },
  approved: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, en: 'Approved', ar: 'معتمد' },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle, en: 'Rejected', ar: 'مرفوض' },
  cancelled: { bg: 'bg-slate-100 text-slate-700', icon: Ban, en: 'Cancelled', ar: 'ملغي' },
  deleted_by_manager: { bg: 'bg-rose-100 text-rose-800', icon: Trash2, en: 'Deleted by manager', ar: 'حذفه المدير' },
  deleted: { bg: 'bg-rose-100 text-rose-800', icon: Trash2, en: 'Deleted by HR', ar: 'حذفته الموارد البشرية' },
};

const STATUS_FILTERS = [
  { value: 'pending', en: 'Awaiting approval', ar: 'بانتظار الموافقة' },
  { value: 'approved', en: 'Approved', ar: 'معتمد' },
  { value: 'rejected', en: 'Rejected', ar: 'مرفوض' },
  { value: 'cancelled', en: 'Cancelled', ar: 'ملغي' },
  { value: 'deleted', en: 'Deleted', ar: 'محذوف' },
];

const EMPTY_FILTERS = { status: '', employee_id: '', leave_type: '' };

// 10 digits starting with 1 (National ID) or 2 (Iqama); Arabic-Indic digits are accepted.
const toWesternDigits = (v) => v.replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
  .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0)).replace(/\s/g, '');
const isIdNumber = (v) => /^[12]\d{9}$/.test(toWesternDigits(v));

const TeamLeavePage = () => {
  const { token } = useAuth();
  const { isRTL, language } = useLanguage();

  const [requests, setRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [idInput, setIdInput] = useState('');
  const [idSearch, setIdSearch] = useState(null); // the number being searched, or null
  const [matched, setMatched] = useState(null);
  const [idError, setIdError] = useState('');

  // Duplicate leave types exist with the same name; filter on every id of the name.
  const typeNames = useMemo(() => {
    const byName = {};
    leaveTypes.forEach((t) => {
      byName[t.name] = byName[t.name] || { name: t.name, name_ar: t.name_ar, ids: [] };
      byName[t.name].ids.push(t.id);
    });
    return Object.values(byName);
  }, [leaveTypes]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    setIdError('');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      let res;
      if (idSearch) {
        res = await axios.post(`${API_URL}/leave/team/requests/by-id-number`, {
          id_number: idSearch, status: filters.status || null, page, page_size: 20,
        }, { headers });
        setMatched(res.data.matched_employee || null);
      } else {
        const params = new URLSearchParams({ page: String(page), page_size: '20' });
        if (filters.status) params.append('status', filters.status);
        if (filters.employee_id) params.append('employee_id', filters.employee_id);
        const type = typeNames.find((t) => t.name === filters.leave_type);
        (type?.ids || []).forEach((id) => params.append('leave_type_id', id));
        res = await axios.get(`${API_URL}/leave/team/requests?${params}`, { headers });
        setMatched(null);
      }
      setRequests(res.data.requests || []);
      setMembers(res.data.members || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (idSearch && (detail?.code === 'NO_MATCH_IN_TEAM' || detail?.code === 'INVALID_ID_NUMBER')) {
        setRequests([]); setTotal(0); setTotalPages(1); setMatched(null);
        setIdError(detail.code === 'NO_MATCH_IN_TEAM'
          ? (isRTL ? 'لا يوجد موظف في فريقك بهذا الرقم' : 'No member of your team has this ID number')
          : (isRTL ? 'أدخل رقم هوية وطنية أو إقامة من 10 أرقام' : 'Enter a 10-digit National ID or Iqama number'));
      } else if (err.response?.status === 429) {
        setError(isRTL ? 'عمليات بحث كثيرة، حاول بعد دقيقة' : 'Too many searches - try again in a minute');
      } else {
        setError(detail?.error
          || (isRTL ? 'تعذّر تحميل طلبات الفريق' : "Could not load your team's requests"));
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, filters, typeNames, isRTL, idSearch]);

  const searchById = (e) => {
    e?.preventDefault();
    if (!isIdNumber(idInput)) {
      setIdError(isRTL ? 'أدخل رقم هوية وطنية أو إقامة من 10 أرقام' : 'Enter a 10-digit National ID or Iqama number');
      return;
    }
    setFilters((prev) => ({ ...prev, employee_id: '', leave_type: '' }));
    setPage(1);
    setIdSearch(toWesternDigits(idInput));
  };
  const clearIdSearch = () => { setIdInput(''); setIdSearch(null); setIdError(''); setMatched(null); setPage(1); };

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  useEffect(() => {
    axios.get(`${API_URL}/leave/types`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLeaveTypes(res.data.leave_types || res.data || []))
      .catch(() => setLeaveTypes([]));
  }, [token]);

  const setFilter = (key, value) => { setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value })); setPage(1); };

  const formatDate = (value) => {
    if (!value) return '-';
    try { return format(new Date(value), 'dd MMM yyyy'); } catch { return value; }
  };
  const memberName = (m) => (language === 'ar' && m.name_ar ? m.name_ar : m.name) || m.id;

  return (
    <div className="space-y-6" data-testid="team-leave-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-slate-500" />
            {isRTL ? 'إدارة الإجازات' : 'Leave Management'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isRTL ? 'جميع طلبات الإجازات المقدمة من موظفي إدارتك' : 'Every leave request from your team'}
          </p>
        </div>
        <Button variant="outline" onClick={fetchRequests} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4 me-2', loading && 'animate-spin')} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" role="alert">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <TeamBalancesCard isRTL={isRTL} />

      <Card data-testid="team-leave-id-search">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />{isRTL ? 'البحث برقم الهوية الوطنية / الإقامة' : 'Search by National ID / Iqama'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={searchById} className="flex flex-wrap items-start gap-2">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <Input
                value={idInput}
                onChange={(e) => { setIdInput(e.target.value); setIdError(''); }}
                inputMode="numeric"
                autoComplete="off"
                maxLength={14}
                dir="ltr"
                aria-label={isRTL ? 'رقم الهوية الوطنية أو الإقامة' : 'National ID or Iqama number'}
                placeholder={isRTL ? 'رقم الهوية أو الإقامة (10 أرقام)' : 'National ID or Iqama (10 digits)'}
                data-testid="team-leave-id-input"
              />
              {idError && <p role="alert" className="text-sm text-red-700 mt-1" data-testid="team-leave-id-error">{idError}</p>}
            </div>
            <Button type="submit" disabled={loading || !idInput.trim()} data-testid="team-leave-id-search-button">
              <Search className="h-4 w-4 me-2" />{isRTL ? 'بحث' : 'Search'}
            </Button>
            {idSearch && (
              <Button type="button" variant="ghost" onClick={clearIdSearch} data-testid="team-leave-id-clear">
                <X className="h-4 w-4 me-1" />{isRTL ? 'إلغاء البحث' : 'Clear search'}
              </Button>
            )}
          </form>
          {matched && (
            <p className="text-sm text-slate-700 mt-3" data-testid="team-leave-id-match">
              {isRTL ? 'طلبات الإجازة للموظف: ' : 'Leave requests of: '}
              <span className="font-medium">{memberName(matched)}</span>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />{isRTL ? 'الفلاتر' : 'Filters'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'حالة الطلب' : 'Status'}</Label>
              <Select value={filters.status || 'all'} onValueChange={(v) => setFilter('status', v)}>
                <SelectTrigger data-testid="team-leave-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  {STATUS_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{isRTL ? s.ar : s.en}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'اسم الموظف' : 'Employee'}</Label>
              <Select value={filters.employee_id || 'all'} onValueChange={(v) => setFilter('employee_id', v)} disabled={!!idSearch}>
                <SelectTrigger data-testid="team-leave-employee-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الموظفين' : 'All employees'}</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{memberName(m)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{isRTL ? 'نوع الإجازة' : 'Leave type'}</Label>
              <Select value={filters.leave_type || 'all'} onValueChange={(v) => setFilter('leave_type', v)} disabled={!!idSearch}>
                <SelectTrigger data-testid="team-leave-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  {typeNames.map((t) => (
                    <SelectItem key={t.name} value={t.name}>{language === 'ar' && t.name_ar ? t.name_ar : t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="ghost" onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}>
                {isRTL ? 'مسح الفلاتر' : 'Clear'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <p className="py-12 text-center text-slate-600" data-testid="team-leave-empty">
              {isRTL ? 'لا توجد طلبات إجازة مطابقة' : 'No matching leave requests'}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table className="min-w-[760px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{isRTL ? 'الموظف' : 'Employee'}</TableHead>
                      <TableHead>{isRTL ? 'نوع الإجازة' : 'Leave type'}</TableHead>
                      <TableHead>{isRTL ? 'من' : 'From'}</TableHead>
                      <TableHead>{isRTL ? 'إلى' : 'To'}</TableHead>
                      <TableHead>{isRTL ? 'المدة' : 'Days'}</TableHead>
                      <TableHead>{isRTL ? 'تاريخ التقديم' : 'Submitted'}</TableHead>
                      <TableHead>{isRTL ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead className="text-end">{isRTL ? 'الإجراءات' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((r) => {
                      const style = STATUS_STYLES[leaveDisplayStatus(r)] || STATUS_STYLES.pending_manager;
                      const Icon = style.icon;
                      return (
                        <TableRow key={r.id} data-testid={`team-leave-row-${r.id}`}>
                          <TableCell className="font-medium">{r.employee_name?.trim() || '-'}</TableCell>
                          <TableCell>{language === 'ar' && r.leave_type_name_ar ? r.leave_type_name_ar : r.leave_type_name}</TableCell>
                          <TableCell>{formatDate(r.start_date)}</TableCell>
                          <TableCell>{formatDate(r.end_date)}</TableCell>
                          <TableCell><Badge variant="secondary">{r.working_days_requested}</Badge></TableCell>
                          <TableCell className="text-slate-500">{formatDate(r.submitted_at || r.created_at)}</TableCell>
                          <TableCell>
                            <Badge className={style.bg}><Icon className="h-3 w-3 me-1" />{isRTL ? style.ar : style.en}</Badge>
                            {r.archive_action === 'delete' && r.archive_reason && (
                              <p className="text-xs text-rose-700 mt-1 max-w-[180px] truncate" title={r.archive_reason}>{r.archive_reason}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setDetails(r)} data-testid={`team-leave-view-${r.id}`}>
                                <Eye className="h-4 w-4 me-1" />{isRTL ? 'عرض' : 'View'}
                              </Button>
                              <ManagerDeleteLeaveButton request={r} isRTL={isRTL} onDeleted={fetchRequests} />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-600">
                    {isRTL ? `عرض ${requests.length} من ${total}` : `Showing ${requests.length} of ${total}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm">{page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <LeaveRequestDetailsDialog
        open={!!details}
        onClose={() => setDetails(null)}
        request={details}
        token={token}
        isRTL={isRTL}
        language={language}
      />
    </div>
  );
};

export default TeamLeavePage;
